import React, { useState } from 'react';

import { Tooltip } from 'antd';

import styled from 'styled-components';

import NwIcon from '@components/Gui/NwIcon';
import InvoiceDrawer from '@components/InvoicesAndCreditNotes/InvoiceDrawer';
import LegacyPaymentDrawer from '@components/Payments/LegacyPaymentDrawer';
import PaymentDrawer from '@components/Payments/PaymentDrawer';
import StatementClientInvoiceStatusIcon from '@components/Transactions/StatementClientInvoiceStatusIcon';
import StatementStatusIcon from '@components/Transactions/StatementStatusIcon';

import { getPayableSubjectStatementStatus, getSubjectStatementStatus } from '../helpers';
import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Wrapper = styled.div`
    min-width: 4rem;
    padding: 0.75rem 0;
    /* min-height: 4rem; */
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .transaction-status-double {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        flex-wrap: nowrap;
        gap: 0.5rem;
    }
`;

const renderPayableInvoiceStatus = ({ transaction, statusModel, onOpenInvoice, getInvoiceIcon, getInvoiceTooltip }) => {
    if (!statusModel.invoiced) {
        return null;
    }

    const canOpenInvoice = typeof onOpenInvoice === 'function' && Boolean(statusModel.invoiceId);
    const icon = getInvoiceIcon
        ? getInvoiceIcon(transaction)
        : statusModel.invoicePosted
          ? byPrefixAndName.fasl['file-export']
          : statusModel.invoiceNumber
            ? byPrefixAndName.fasl['file-invoice-dollar']
            : byPrefixAndName.fasl['lock'];
    const tooltip = getInvoiceTooltip
        ? getInvoiceTooltip(transaction)
        : statusModel.invoiceNumber
          ? `Invoice #${statusModel.invoiceNumber}`
          : 'In a invoice draft';

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenInvoice(statusModel.invoiceId);
    };

    return (
        <div className="model-invoice-status">
            <Tooltip title={tooltip}>
                <NwIcon pointer={canOpenInvoice} icon={icon} onClick={canOpenInvoice ? handleClick : undefined} />
            </Tooltip>
        </div>
    );
};

const renderPayablePaymentStatus = ({ transaction, statusModel, onOpenPayment, onOpenNw3Payment, getPaymentTooltip }) => {
    if (!statusModel.paid) {
        return null;
    }

    if (statusModel.paymentSource === 'nw3') {
        // IsInNw3PaymentIDs may list multiple Nw3 payments; fall back to the single legacy ID
        const nw3PaymentIds = statusModel.nw3PaymentIds?.length
            ? statusModel.nw3PaymentIds
            : statusModel.nw3PaymentId
              ? [statusModel.nw3PaymentId]
              : [];
        const canOpenNw3Payment = typeof onOpenNw3Payment === 'function' && nw3PaymentIds.length > 0;
        const handleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenNw3Payment(nw3PaymentIds);
        };

        return (
            <div className="model-payment-status">
                <Tooltip placement="right" title="This line is in a payment imported from Netwalk 3">
                    <NwIcon
                        danger
                        pointer={canOpenNw3Payment}
                        icon={byPrefixAndName.fasl['money-check-dollar-pen']}
                        onClick={canOpenNw3Payment ? handleClick : undefined}
                    />
                </Tooltip>
            </div>
        );
    }

    if (statusModel.paymentId) {
        const canOpenPayment = typeof onOpenPayment === 'function';
        const handleClick = (event) => {
            event.preventDefault();
            event.stopPropagation();
            onOpenPayment(statusModel.paymentId);
        };

        return (
            <div className="model-payment-status">
                <Tooltip
                    title={getPaymentTooltip ? getPaymentTooltip(transaction) : `Payment #${statusModel.paymentNumber}`}
                >
                    <NwIcon
                        pointer={canOpenPayment}
                        icon={byPrefixAndName.fasl['money-check-dollar-pen']}
                        onClick={canOpenPayment ? handleClick : undefined}
                    />
                </Tooltip>
            </div>
        );
    }

    return (
        <div className="model-payment-status">
            <Tooltip title="In a legacy payment">
                <NwIcon icon={byPrefixAndName.fasl['money-check-dollar-pen']} />
            </Tooltip>
        </div>
    );
};

const renderStatementInvoiceStatus = ({ statement, statusModel, onOpenInvoice, getInvoiceTooltip }) => {
    if (!statusModel.invoiceId) {
        return null;
    }

    const canOpenInvoice = typeof onOpenInvoice === 'function';
    let icon = byPrefixAndName.fasl['file-invoice-dollar'];
    if (statement?.InvoicePosted) {
        icon = byPrefixAndName.fasl['file-export'];
    } else if (!statusModel.invoiceNumber || statusModel.state === 'locked') {
        icon = byPrefixAndName.fasl['lock'];
    }

    const tooltip = getInvoiceTooltip
        ? getInvoiceTooltip(statement)
        : statusModel.invoiceNumber
          ? `Invoice #${statusModel.invoiceNumber}`
          : 'In a invoice draft';

    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenInvoice(statusModel.invoiceId);
    };

    return (
        <div className="model-invoice-status">
            <Tooltip title={tooltip}>
                <NwIcon pointer={canOpenInvoice} icon={icon} onClick={canOpenInvoice ? handleClick : undefined} />
            </Tooltip>
        </div>
    );
};

const renderStatementPaymentStatus = ({ statement, statusModel, onOpenPayment, getPaymentTooltip }) => {
    if (statusModel.paidState !== 'paid' && statusModel.paidStatusName !== 'Paid') {
        return null;
    }

    const canOpenPayment = typeof onOpenPayment === 'function' && Boolean(statusModel.paymentId);
    const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpenPayment(statusModel.paymentId);
    };
    const tooltip = getPaymentTooltip
        ? getPaymentTooltip(statement)
        : statusModel.paymentNumber
          ? `Payment #${statusModel.paymentNumber}`
          : 'Payment';

    return (
        <div className="model-payment-status">
            <Tooltip title={tooltip}>
                <NwIcon
                    pointer={canOpenPayment}
                    icon={byPrefixAndName.fasl['money-check-dollar-pen']}
                    onClick={canOpenPayment ? handleClick : undefined}
                />
            </Tooltip>
        </div>
    );
};

const getReceivableStatusModel = (statusModel) => {
    let state = statusModel.state;

    if (statusModel.paidState === 'paid' || statusModel.paidStatusName === 'Paid' || statusModel.state === 'paid') {
        state = 'paid';
    } else if (statusModel.state === 'invoiced') {
        state = 'billed';
    }

    return {
        kind: 'receivable-subject',
        state,
        clientId: true,
        invoiceId: statusModel.invoiceId,
        invoiceNumber: statusModel.invoiceNumber,
        invoiceDate: statusModel.invoiceDate,
        invoicePosted: Boolean(statusModel.invoicePosted),
        raw: statusModel.raw,
    };
};

const SubjectStatementStatusCell = ({
    statement,
    transaction,
    mode = 'statement',
    statementDirection = 'payable',
    compact = false,
    additionalText,
    small,
    getInvoiceIcon,
    getInvoiceTooltip,
    getPaymentTooltip,
    disableDetailOpen = false,
    detailContactType = 'model',
    invoiceDrawerProps,
    onInvoiceClose,
    onPaymentClose,
}) => {
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [selectedNw3PaymentIds, setSelectedNw3PaymentIds] = useState(null);

    const handleOpenInvoice = (invoiceId) => {
        if (!disableDetailOpen && invoiceId) {
            setSelectedInvoice(invoiceId);
        }
    };

    const handleOpenPayment = (paymentId) => {
        if (!disableDetailOpen && paymentId) {
            setSelectedPayment(paymentId);
        }
    };

    const handleOpenNw3Payment = (nw3PaymentIds) => {
        if (!disableDetailOpen && nw3PaymentIds?.length) {
            setSelectedNw3PaymentIds(nw3PaymentIds);
        }
    };

    const handleCloseInvoice = () => {
        setSelectedInvoice(null);
        if (typeof onInvoiceClose === 'function') {
            onInvoiceClose();
        }
    };

    const handleClosePayment = () => {
        setSelectedPayment(null);
        if (typeof onPaymentClose === 'function') {
            onPaymentClose();
        }
    };

    // No onPaymentClose here: the nw3 drawer is read-only, no need to reload the statement
    const handleCloseNw3Payment = () => {
        setSelectedNw3PaymentIds(null);
    };

    if (mode === 'payable-transaction') {
        const statusModel = getPayableSubjectStatementStatus(transaction);
        const className = compact ? 'transaction-model-status compact' : 'transaction-model-status';

        if (statusModel.isPaymentRow) {
            return <div className={className} />;
        }

        const content = (
            <div className={className}>
                {renderPayableInvoiceStatus({
                    transaction,
                    statusModel,
                    onOpenInvoice: disableDetailOpen ? undefined : handleOpenInvoice,
                    getInvoiceIcon,
                    getInvoiceTooltip,
                })}
                {renderPayablePaymentStatus({
                    transaction,
                    statusModel,
                    onOpenPayment: disableDetailOpen ? undefined : handleOpenPayment,
                    onOpenNw3Payment: disableDetailOpen ? undefined : handleOpenNw3Payment,
                    getPaymentTooltip,
                })}
            </div>
        );

        return (
            <>
                {content}
                {selectedInvoice && (
                    <InvoiceDrawer invoiceId={selectedInvoice} onClose={handleCloseInvoice} {...invoiceDrawerProps} />
                )}
                {selectedPayment && (
                    <PaymentDrawer
                        paymentID={selectedPayment}
                        contactType={detailContactType}
                        onClose={handleClosePayment}
                    />
                )}
                {selectedNw3PaymentIds && (
                    <LegacyPaymentDrawer nw3PaymentIds={selectedNw3PaymentIds} onClose={handleCloseNw3Payment} />
                )}
            </>
        );
    }

    const statusModel = getSubjectStatementStatus(statement);
    const isReceivableStatement = statementDirection === 'receivable';

    if (isReceivableStatement) {
        const receivableStatusModel = getReceivableStatusModel(statusModel);
        return (
            <>
                <Wrapper className="transaction-status">
                    <StatementClientInvoiceStatusIcon
                        statusModel={receivableStatusModel}
                        canOpenInvoice={!disableDetailOpen}
                        onInvoiceClick={disableDetailOpen ? undefined : handleOpenInvoice}
                        additionalText={additionalText}
                        small={small}
                    />
                </Wrapper>
                {selectedInvoice && (
                    <InvoiceDrawer invoiceId={selectedInvoice} onClose={handleCloseInvoice} {...invoiceDrawerProps} />
                )}
            </>
        );
    }

    if (mode === 'statement-with-payment') {
        const className = compact ? 'transaction-model-status compact' : 'transaction-model-status';

        return (
            <>
                <Wrapper className={className}>
                    <div className="transaction-status-double">
                        {renderStatementInvoiceStatus({
                            statement,
                            statusModel,
                            onOpenInvoice: disableDetailOpen ? undefined : handleOpenInvoice,
                            getInvoiceTooltip,
                        })}
                        {renderStatementPaymentStatus({
                            statement,
                            statusModel,
                            onOpenPayment: disableDetailOpen ? undefined : handleOpenPayment,
                            getPaymentTooltip,
                        })}
                    </div>
                </Wrapper>
                {selectedInvoice && (
                    <InvoiceDrawer invoiceId={selectedInvoice} onClose={handleCloseInvoice} {...invoiceDrawerProps} />
                )}
                {selectedPayment && (
                    <PaymentDrawer
                        paymentID={selectedPayment}
                        contactType={detailContactType}
                        onClose={handleClosePayment}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <Wrapper className="transaction-status">
                <StatementStatusIcon
                    statement={statement}
                    statusModel={statusModel}
                    onInvoiceClick={disableDetailOpen ? undefined : handleOpenInvoice}
                    additionalText={additionalText}
                    small={small}
                />
            </Wrapper>
            {selectedInvoice && (
                <InvoiceDrawer invoiceId={selectedInvoice} onClose={handleCloseInvoice} {...invoiceDrawerProps} />
            )}
        </>
    );
};

export default SubjectStatementStatusCell;
